"use client"

import React from "react"
import { X } from "lucide-react"
import { describeFilter, hasFilterState, type FilterValues } from "@/lib/filter-values"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsFilterDefinition } from "@/types/ats-explorer-api"

export interface ActiveFilterChipsProps {
  definitions: AtsFilterDefinition[]
  values: FilterValues
  /** Free-text keyword shown as its own chip */
  searchText?: string
  onRemove: (name: string) => void
  onClearSearch?: () => void
  onClearAll: () => void
  /** Clicking a chip opens the filter blade on that filter */
  onEdit?: (name: string) => void
}

/**
 * The Timesheet Hub's active-filter row: an uppercase ACTIVE label, one removable
 * chip per filter (labels captured at pick time, so a reference never shows a raw
 * id), and "Clear all". Rendered under the toolbar so the applied query is always
 * visible while records load live.
 */
export function ActiveFilterChips({ definitions, values, searchText, onRemove, onClearSearch, onClearAll, onEdit }: ActiveFilterChipsProps) {
  const { t } = useTranslation()
  const byName = new Map(definitions.map((d) => [d.name, d] as const))
  const active = Object.entries(values).filter(([name, state]) => byName.has(name) && hasFilterState(state))
  const hasSearch = !!searchText?.trim()

  if (active.length === 0 && !hasSearch) return null

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
      <span className="th-section-label text-[var(--th-label)]">{t("explorer.chips.active")}</span>
      {hasSearch && (
        <Chip label={`“${searchText!.trim()}”`} onRemove={onClearSearch} />
      )}
      {active.map(([name, state]) => (
        <Chip key={name} label={describeFilter(byName.get(name)!, state, t)} onClick={onEdit ? () => onEdit(name) : undefined} onRemove={() => onRemove(name)} />
      ))}
      <button type="button" className="px-1.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:underline" onClick={onClearAll}>
        {t("explorer.chips.clearAll")}
      </button>
    </div>
  )
}

/** The hub's removable pill, shared with the multi-value filter inputs */
export function Chip({ label, onClick, onRemove }: { label: string; onClick?: () => void; onRemove?: () => void }) {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border border-chip-border bg-chip py-1 pl-3 pr-1.5 text-xs font-semibold text-[var(--th-active-pill-fg)]">
      {onClick ? (
        <button type="button" className="max-w-64 truncate hover:underline" onClick={onClick} title={label}>
          {label}
        </button>
      ) : (
        <span className="max-w-64 truncate" title={label}>{label}</span>
      )}
      {onRemove && (
        <button
          type="button"
          className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-chip-remove text-[var(--th-active-pill-fg)] hover:brightness-95"
          aria-label={t("explorer.common.removeAria", { name: label })}
          onClick={onRemove}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  )
}
